import React, { useEffect, useState } from 'react';
import './search-statement-dialog.css';
import Tooltip from '../../../lib/tooltip/Tooltip';
import {
	ITabManager,
	useSearchStatementContext,
	withSearchStatementProvider,
} from './search-statement-context';
import { EditorService } from '../../../services/editor';
import DataAggregationSection from './DataAggregationSection';
import FilterConditionsSection from './FilterConditionsSection';
import OutputFormatSection from './OutputFormatSection';
import ResultsSection from './ResultsSection';
import Button from '../../../lib/button/Button';
import MainSourceAndInputsSection from './MainSourceAndInputsSection';
import EntityMetadataSection from './EntityMetadataSection';
import { Alert } from '@mui/material';
import { popupNotification } from '../../../popup-notification';

export interface ISearchStatementBuilderProps {
	project: EditorService;
	ready: boolean;
	tabManager: ITabManager;
	onClose?: (e: any) => void;
}

const SearchStatementBuilder: React.FC<ISearchStatementBuilderProps> = ({
	onClose,
}) => {
	const { project, entity, searchStatementState } = useSearchStatementContext();

	const useForceUpdate = () => {
		const [_, setValue] = useState(0);
		return () => setValue((value) => value + 1);
	};
	const forceUpdate = useForceUpdate(); // Update the component

	useEffect(() => {
		project?.on('value-written', ({ owner, value: _ }: any = {}) => {
			if ((entity?.inputs || []).includes(owner)) {
				forceUpdate();
			}
		});

		project?.on('value-removed', ({ owner }: any = {}) => {
			if ((entity?.inputs || []).includes(owner)) {
				forceUpdate();
			}
		});
	}, [entity]);

	if (!project) {
		return null;
	}

	const validationErrors = searchStatementState?.validationErrors || [];

	return (
		<div
			style={{
				display: 'flex',
				flexDirection: 'column',
				height: '100%',
				gap: '4px',
				justifyContent: 'space-between',
				width: '100%',
				minWidth: 0,
			}}
		>
			<span
				style={{
					width: '100%',
					maxWidth: '100%',
					minWidth: 0,
					maxHeight: '100%',
					overflowY: 'scroll',
					overflowX: 'hidden',
					display: 'flex',
					flexDirection: 'column',
					height: '100%',
					paddingBottom: 20
				}}
			>
				<MainSourceAndInputsSection />

				<hr
					style={{ margin: '20px 0 0 0', border: '1px solid rgba(0,0,0,0.08)' }}
				/>

				<EntityMetadataSection />

				<hr style={{ margin: '1px 0', border: '1px solid rgba(0,0,0,0.08)' }} />

				<DataAggregationSection />

				<hr style={{ margin: '1px 0', border: '1px solid rgba(0,0,0,0.08)' }} />

				<FilterConditionsSection />

				<hr style={{ margin: '1px 0', border: '1px solid rgba(0,0,0,0.08)' }} />

				<OutputFormatSection />

				<hr style={{ margin: '1px 0', border: '1px solid rgba(0,0,0,0.08)' }} />

				{validationErrors.length > 0 && (
					<div
						style={{
							marginTop: 20,
							marginRight: 40,
							marginLeft: 80,
							marginBottom: 20,
						}}
					>
						<Alert severity='error'>
							{validationErrors.length === 1 ? (
								<span
									style={{
										display: 'flex',
										alignItems: 'center',
										gap: 6,
									}}
								>
									<h4
										style={{
											margin: 0,
											display: 'flex',
										}}
									>
										Error:
									</h4>
									<p
										style={{
											margin: 0,
											display: 'flex',
										}}
									>
										{validationErrors[0]}
									</p>
								</span>
							) : (
								<>
									<h4
										style={{
											margin: 0,
										}}
									>
										Errors
									</h4>
									<br className='quarter-space' />
									<ul
										style={{
											listStyleType: 'none',
											padding: 0,
											margin: 0,
										}}
									>
										{validationErrors.map((error, index) => (
											<li
												key={index}
												style={{
													display: 'flex',
													alignItems: 'center',
												}}
											>
												{/* Circle */}
												<span
													style={{
														fontSize: 10,
														marginRight: 5,
													}}
												>
													&#9679;
												</span>
												<p
													style={{
														margin: 0,
													}}
												>
													{error}
												</p>
											</li>
										))}
									</ul>
								</>
							)}
						</Alert>
					</div>
				)}

				<ResultsSection />
			</span>

			<div
				style={{
					alignSelf: 'flex-start',
					display: 'flex',
					flexDirection: 'row',
					justifyContent: 'flex-end',
					width: '100%',
					gap: '16px',
					padding: '0 40px',
				}}
			>
				<Button
					id='search-statement-dialog-save-query-button'
					onClick={(e: any) => {
						const queryString = searchStatementState.toQuery();

						navigator.clipboard.writeText(queryString);

						popupNotification({
							text: 'The query has been copied to the clipboard',
						});
					}}
					disabled={validationErrors.length > 0}
					color='primary'
					variant='outlined'
				>
					Copy as SQL
				</Button>
				<Tooltip
					anchorQuerySelector='#search-statement-dialog-save-query-button'
					placement='bottom'
					openDelay={300}
				>
					Copy as a native SQL query
				</Tooltip>
				<Button
					id='search-statement-dialog-done-button'
					onClick={(e: any) => {
						onClose?.(e);
					}}
					color='primary'
				>
					Done
				</Button>
				<Tooltip
					anchorQuerySelector='#search-statement-dialog-done-button'
					placement='bottom'
					openDelay={300}
				>
					Save the changes and exit the search statement builder
				</Tooltip>
			</div>
		</div>
	);
};

export default withSearchStatementProvider<ISearchStatementBuilderProps>(
	SearchStatementBuilder,
);
